import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { AutocompleteGetTagProps, styled } from '@mui/material';

import { useAutocomplete } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CheckIcon from '@mui/icons-material/Check';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CancelIcon from '@mui/icons-material/Cancel';
import useDebounce from '../../hooks/useDebounce';

const RootContainer = styled('div')(
  ({ theme }) => `
    color: ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,.85)'
    };
    font-size: 14px;
    margin: 0px 8px;
  `,
);

const InputWrapper = styled('div')(
  ({ theme }) => `
  min-width: 320px;
  max-width: 600px;
  min-height: 40px;
  border: 1px solid ${theme.palette.mode === 'dark' ? '#87969C' : '#87969C'};
  background-color: ${theme.palette.mode === 'dark' ? '#F3F7F9' : '#F3F7F9'};
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  &:hover:not(.disabled) {
    border-color: ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.87)'};
  }

  &.focused:not(.disabled) {
    border-color: ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.87)'};
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &.disabled {
    border-color: #EBF1F5;
    background-color: #F3F7F9;
  }

  & input:disabled {
    cursor: not-allowed;
    background-color: #F3F7F9;
    color: #ACC0CA;
  }

  &.autocomplete-error {
    border-color: red;
  }

  & input {
    background-color: ${theme.palette.mode === 'dark' ? '#F3F7F9' : '#F3F7F9'};
    color: ${theme.palette.mode === 'dark' ? '#5B6971' : '#5B6971'
    };

    &:disabled::placeholder {
      color: #ACC0CA;
    }

    box-sizing: border-box;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
    min-width: 50px;
  }
`,
);

interface TagProps extends ReturnType<AutocompleteGetTagProps> {
  label: string;
}

function Tag(props: TagProps) {
  const { label, onDelete, disabled, ...other } = props;
  return (
    <div {...other}>
      <span>{label}</span>
      {
        !disabled && <CancelIcon onClick={onDelete} fontSize="large" htmlColor="#5B6971" />
      }
    </div>
  );
}

const StyledTag = styled(Tag, {shouldForwardProps: (prop) => (prop !== 'disabled')})<TagProps>(
  ({ theme, disabled }) => `
    display: flex;
    align-items: center;
    height: 28px;
    margin-left: 4px;
    margin-top: 4px;
    margin-bottom: 4px;
    line-height: 22px;
    background-color: ${theme.palette.mode === 'dark' ? '#E4EAEE' : '#E4EAEE'
    };
    border-radius: 60px;
    color: #5B6971;
    box-sizing: content-box;
    outline: 0;
    overflow: hidden;
  
    &:focus {
      border-color: ${theme.palette.mode === 'dark' ? '#177ddc' : '#40a9ff'};
      background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
    }
  
    & span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-family: 'Open Sans';
      font-size: 12px;
      line-height: 20px;
      padding-left: 10px;
      padding-right: ${disabled ? '10px' : '4px'};
    }
  
    & svg {
      font-size: 14px;
      cursor: pointer;
      margin-left: 4px;
      margin-right: 8px;
    }
  `,
);

const Listbox = styled('ul')(
  ({ theme }) => `
    width: 320px;
    margin: 2px 0 0;
    padding: 0;
    position: absolute;
    list-style: none;
    background-color: ${theme.palette.mode === 'dark' ? '#141414' : '#fff'};
    overflow: auto;
    max-height: 250px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1;
    scroll-snap-stop: always;
    font-size: 14px;
  
    & li {
      padding: 5px 12px;
      display: flex;
      cursor: pointer;
      font-size: 14px;

      &:hover {
        background-color: #eee;
      }
  
      & span {
        flex-grow: 1;
        font-size: 14px;
      }
  
      & svg {
        color: transparent;
      }
    }
  
    & li[aria-selected='true'] {
      background-color: ${theme.palette.mode === 'dark' ? '#2b2b2b' : '#fafafa'};
      font-weight: 600;
  
      & svg {
        color: #1890ff;
      }
    }
  
    & li[data-focus='true'] {
      background-color: ${theme.palette.mode === 'dark' ? '#003b57' : '#e6f7ff'};
      cursor: pointer;
  
      & svg {
        color: currentColor;
      }
    }
  `,
);

export interface AutoCompleteProps<T> {
  fieldLabel: string
  placeholder?: string
  options: Array<T>
  defaultValue?: Array<T> | T
  value: Array<T> | T
  disabled?: boolean
  multiple: boolean
  getOptionLabel: (option: T) => string
  getValueLabel?: (option: T) => string
  noResults?: boolean
  debounceInput?: boolean
  hasError?: boolean
  isOptionEqualToValue?: (option: T, value: T) => boolean

  loadingResults?: boolean
  loadingNextPage?: boolean
  paginated?: boolean
  currentPage?: number
  debounceTimeoutMS?: number
  selectedIdKey: string
  onKeyStroke: (value: string) => void
  onFetchResults?: (value: string) => void
  onFetchNextPage?: (value: string, page: number) => void
  onSelect: (option: T | Array<T>) => void
  onOpen?: () => void

  ariaLabelNoResults: string
}

export const AutoComplete = <T extends unknown>(props: AutoCompleteProps<T>) => {
  const [input, setInput] = useState('')
  const scrollBoxRef = useRef(null)
  const scrollRef = useRef(0)
  const lastPageLength = useRef(0)

  const debouncedSearch = useDebounce(input, 500)

  const handleClose = () => {
    scrollRef.current = 0
  }

  const customGetOptionLabel = (option: T): string => {
    return props.getOptionLabel(option) || props.placeholder
  }

  useEffect(() => {
    if (props.debounceInput)
      props.onKeyStroke(debouncedSearch)
  }, [debouncedSearch])

  const handleKeyStroke = (e: any, val: string) => {
    if (props.debounceInput) {
      setInput(val)
    } else {
      props.onKeyStroke(val)
    }
  }

  const {
    getRootProps,
    getInputProps,
    getTagProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    focused,
    inputValue,
    setAnchorEl,
  } = useAutocomplete({
    autoHighlight: true,
    multiple: props.multiple,
    options: props.options,
    getOptionLabel: customGetOptionLabel,
    disableCloseOnSelect: props.multiple,
    onClose: handleClose,
    onInputChange: handleKeyStroke,
    openOnFocus: true,
    isOptionEqualToValue: props.isOptionEqualToValue,
    onOpen: props.onOpen,
    // @ts-ignore
    value: props.value,
    onChange: (e, val, reason) => { props.onSelect(val as T | Array<T>); lastPageLength.current = 0 },
  });

  const handleListBoxScroll = (event: React.SyntheticEvent) => {
    if (!props.paginated) return;

    const listboxNode = event.currentTarget;
    scrollRef.current = listboxNode.scrollTop
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight && 
      !props.loadingNextPage &&
      typeof props.onFetchNextPage === 'function' && 
      props.options.length !== lastPageLength.current
    ) {
      props.onFetchNextPage(inputValue, props.currentPage)
      lastPageLength.current = props.options.length
    }
  }

  // when the new page results come back we want to maintain where the user was scrolled in the list
  useLayoutEffect(() => {
    if(scrollBoxRef.current)
      scrollBoxRef.current.scrollTop = scrollRef.current
  }, [props.options.length])

  // when the user starts typing we want to scroll them back up to the top of the list
  useLayoutEffect(() => {
    if(scrollBoxRef.current)
      scrollBoxRef.current.scrollTop = 0
    scrollRef.current = 0
    lastPageLength.current = 0
  }, [inputValue])

  let renderPlaceHolder = ''
  if (props.placeholder) {
    if (props.multiple) {
      // @ts-ignore
      if (props.value && props.value.length === 0 && inputValue === '') {
        renderPlaceHolder = props.placeholder
      }
    } else {
      if (inputValue === '') {
        renderPlaceHolder = props.placeholder
      }
    }
  }

  return (
    <RootContainer>
      <div {...getRootProps()}>
        <InputWrapper ref={setAnchorEl} className={`${focused ? 'focused ' : ' '} ${props.disabled ? 'disabled' : ' '} ${props.hasError ? 'autocomplete-error' : ' '}`}>
          { /* @ts-ignore */ }
          {props.multiple && props.value && props.value.map((option: any, index: number) => (
            <StyledTag key={props.getValueLabel(option)} label={props.getValueLabel(option)} disabled={props.disabled} {...getTagProps({ index })} />
          ))}
          <input 
            {...getInputProps()} 
            placeholder={renderPlaceHolder} 
            disabled={props.disabled}
          />
          {props.loadingResults && (
            <div style={{ display: 'flex', alignItems: 'center', marginRight: '5px' }}>
              <CircularProgress size={17} />
            </div>
          )}
          <ArrowDropDownIcon />
        </InputWrapper>
      </div>
      {groupedOptions.length > 0 && !props.noResults ? (
        <Listbox
          {...getListboxProps()}
          ref={scrollBoxRef}
          onScroll={handleListBoxScroll}
        >
          {(groupedOptions as typeof props.options).map((option, index) => (
            <li {...getOptionProps({ option, index })}>
              <span>{props.getOptionLabel(option)}</span>
              <CheckIcon fontSize="small" />
            </li>
          ))}
          {props.loadingNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '5px', paddingBottom: '15px' }}>
              <CircularProgress size={17} />
            </div>
          )}
        </Listbox>
      ) : null}
      {
        props.noResults && focused && (
          <Listbox {...getListboxProps()}>
            <li>
              {
                props.loadingResults ? (
                  <CircularProgress size={17} />
                ) : (
                  <span>{props.ariaLabelNoResults}</span>
                )
              }
            </li>
          </Listbox>
        )
      }
    </RootContainer>
  );
};

export default AutoComplete;
